import type { Ref } from "vue";

const initializeWithFn = <T>(fn: () => Promise<T>, key?: string) => {
  let ready: Ref<boolean>,
    targetState: Ref<T | undefined>,
    state: Ref<T | undefined>;
  if (key) {
    ready = useState(`${key}-ready`, () => false);
    targetState = useState<T>(`${key}-target`);
    state = useState<T>(`${key}-state`);
  } else {
    ready = ref(false);
    targetState = ref<T>();
    state = ref<T>();
  }

  if (!ready.value)
    fn().then((res) => {
      ready.value = true;
      targetState.value = res;
      state.value = res;
    });

  return {
    ready,
    targetState,
    state,
  };
};

export const useGenericSyncedState = <
  T extends number | string | object | boolean,
>(
  check: () => Promise<T>,
  update: (value: T, signal: AbortSignal) => Promise<T | undefined>,
  key?: string,
  stateEqual = (a: T | undefined, b: T | undefined) => a === b,
) => {
  const { ready, targetState, state } = initializeWithFn(check, key);

  const abortController = ref<AbortController | undefined>();

  const updateState = (value: T) => {
    if (abortController.value) {
      abortController.value.abort("state updated externally");
    }
    if (stateEqual(value, targetState.value)) return;

    targetState.value = value;
    abortController.value = new AbortController();
    const signal = abortController.value.signal;

    new Promise<T | undefined>((resolve, reject) => {
      // If the signal is already aborted, immediately throw in order to reject the promise.
      if (signal.aborted) {
        reject(signal.reason);
      }

      update(value, signal)
        .then((res) => resolve(res))
        .catch((e) => reject(e));

      signal.addEventListener("abort", () => {
        reject(signal.reason);
      });
    })
      .then((res) => {
        // undefined = do nothing
        if (res !== undefined) {
          if (res !== targetState.value)
            throw new Error(
              "State sync mismatch, the update function did not return the expected result",
            );
          state.value = res;
        }
      })
      .catch((e) => {
        // TODO: maybe do something
        targetState.value = state.value;
      })
      .finally(() => {
        abortController.value = undefined;
      });
  };

  /*
  ready: boolean flag if sync is initialized
  targetState: the state that the handler is syncing to
  state: the current state
  pending: flag if the handler is syncing
  updateState: fn to update state to new value, sets targetState = new value
   */
  return {
    ready: readonly(ready),
    targetState: readonly(targetState),
    state: readonly(state),
    pending: computed(() => !!abortController.value),
    updateState,
  };
};
